import { useMemo, useRef, useState } from 'react';
import { useCreateOrPublishJobPosting } from '../../../../../domain/PermanentJobs/useCases';
import { UserSubProfession } from '../../../../../enums/UserProfession';
import { CompensationType, jobDescriptionMinLength } from '../../shared/constants';
import { isSubProfession } from '../../shared/utils';

const initialForm = {
  office: null,
  recruiterEmail: '',
  planId: '',
  jobTitle: '',
  professionType: '',
  subProfession: '',
  officeSpecialty: '',
  payType: 'Hourly',
  compensationType: '',
  minRate: null,
  maxRate: null,
  benefits: [],
  benefitsOther: '',
  scheduleType: '',
  expectedDays: [],
  jobDescription: {
    raw: '',
    text: '',
  },
};

export const usePostPermanentJobForm = (defaultValues = {}) => {
  const [formValues, setFormValues] = useState({
    ...initialForm,
    ...defaultValues,
  });
  const createdPostingData = useRef({ officeId: '', postingId: '' });

  const handleChange = (field, value) => {
    if (field === 'compensationType' && value === CompensationType.FIXED) {
      setFormValues((prev) => ({
        ...prev,
        [field]: value,
        maxRate: null,
      }));
      return;
    }

    if (field === 'benefits' && !value.includes('Other')) {
      setFormValues((prev) => ({
        ...prev,
        [field]: value,
        benefitsOther: '',
      }));
      return;
    }

    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleProfessionChange = (professionType) => {
    if (isSubProfession(professionType)) {
      setFormValues((prev) => ({
        ...prev,
        professionType: UserSubProfession.getProfession(professionType),
        subProfession: professionType,
      }));
    } else {
      setFormValues((prev) => ({
        ...prev,
        professionType,
        subProfession: '',
      }));
    }
  };

  const handleJobDescriptionChange = (raw, text) => {
    setFormValues((prev) => ({
      ...prev,
      jobDescription: {
        raw,
        text,
      },
    }));
  };

  const mutation = useCreateOrPublishJobPosting({
    onSuccess(postingId, { officeId }) {
      createdPostingData.current = {
        officeId,
        postingId,
      };

      setFormValues(initialForm);
    },
  });

  const errorStatus = useMemo(() => {
    const invalidOffice = !formValues.office;
    const invalidJobTitle = !formValues.jobTitle;
    const invalidProfessionType = !formValues.professionType;
    const invalidOfficeSpecialty = !formValues.officeSpecialty;
    const invalidPayType = !formValues.payType;
    const invalidCompensationType = !formValues.compensationType;
    const invalidMinRate = !formValues.minRate || formValues.minRate <= 0;
    const invalidMaxRate =
      (formValues.compensationType === CompensationType.RANGE && !formValues.maxRate) ||
      (formValues.minRate && parseInt(formValues.maxRate, 10) < parseInt(formValues.minRate, 10));
    const invalidScheduleType = !formValues.scheduleType;
    const invalidJobDescription =
      !formValues.jobDescription?.text ||
      formValues.jobDescription.text.trim().length < jobDescriptionMinLength;
    const invalidPaymentPlan = !formValues.planId;

    const invalidForm =
      invalidOffice ||
      invalidJobTitle ||
      invalidProfessionType ||
      invalidOfficeSpecialty ||
      invalidPayType ||
      invalidCompensationType ||
      invalidMinRate ||
      invalidMaxRate ||
      invalidScheduleType ||
      invalidJobDescription ||
      invalidPaymentPlan;

    return {
      invalidForm,
      invalidPaymentPlan,
      invalidOffice,
      invalidJobTitle,
      invalidProfessionType,
      invalidOfficeSpecialty,
      invalidPayType,
      invalidCompensationType,
      invalidMinRate,
      invalidMaxRate,
      invalidScheduleType,
      invalidJobDescription,
    };
  }, [formValues]);

  return {
    formValues,
    createdPostingData,
    setFormValues,
    handleChange,
    handleProfessionChange,
    handleJobDescriptionChange,
    mutation,
    errorStatus,
  };
};
